import React, { ReactNode, useState, useEffect } from 'react';
import { gql, useFragment } from '@apollo/client';
import { ContactHeroFragment } from '../../generated/graphql';
import { AverageStarRatingAndTotal, STAR_RATING_SECTION } from '../shared';
import { PROFILE_TIERS } from './constants';
import Hero, {
  AgencyDetailsSectionSmall,
  AgencyDetailsSticky,
  ContactBanner,
  ContactDetailsSection,
  ContactDetailsSticky,
  ContactDetailsPlatinumStickyReviews,
  Social,
} from './components';
import { useIsMobile } from '../../hooks';
import { OverallStarRating } from '../shared/recommendations/types';

export type Props = {
  agentIdV2: string;
  agencyId: number;
  renderComponent: ReactNode;
  emailFormComponentId: string;
  desktopWidth?: number;
  onAgencyAddressClick?: () => void;
  onAgencyLogoClick?: () => void;
  onCallButtonClick?: () => void;
  onEmailButtonClick?: () => void;
  onVendorTotalReviewsClick?: () => void;
  onSocialClick?: () => void;
  overallStarRating?: OverallStarRating;
};

const TYPE_NAME = 'Contact';
const FRAGMENT_NAME = 'ContactHero';
const FRAGMENT = gql`
  fragment ContactHero on Contact {
    id
    agentIdV2
    agencyId

    name
    agencyName
    jobCategory

    telephone
    mobile
    hasEmail
    address {
      displayAddress
    }

    profilePhoto {
      url
    }

    agencyLogo
    agencyUrlPath
    agencyByAgencyId {
      hideOfficeLocation

      branding {
        backgroundColour
        logo {
          url
        }
        hero {
          url
        }
      }

      youtubeChannelUrl
      website
    }

    facebookUrl
    twitterUrl
    linkedInUrl
    instagramUrl
    youTubeChannelUrl
    agentVideoUrl
    personalWebsiteUrl
    profileTier
  }
`;

const ContactHeroWithFragment = ({
  agentIdV2,
  agencyId,
  renderComponent,
  emailFormComponentId,
  desktopWidth,
  onAgencyAddressClick = () => undefined,
  onAgencyLogoClick = () => undefined,
  onCallButtonClick = () => undefined,
  onEmailButtonClick = () => undefined,
  onVendorTotalReviewsClick = () => undefined,
  onSocialClick = () => undefined,
  overallStarRating,
}: Props): JSX.Element | null => {
  const isMobile = useIsMobile();
  const [isStickyActive, setStickyActive] = useState(false);
  const [showStarRating, setShowStarRating] = useState(false);

  const { complete, data: contact } = useFragment<ContactHeroFragment>({
    from: {
      __typename: TYPE_NAME,
      id: `${agencyId}-${agentIdV2}`,
    },
    fragment: ContactHeroWithFragment.fragment,
    fragmentName: ContactHeroWithFragment.fragmentName,
  });

  useEffect(() => {
    const showRating =
      contact.profileTier === PROFILE_TIERS.platinum || isMobile;
    setShowStarRating(showRating);
  }, [isMobile, contact.profileTier]);

  if (!complete || !contact) {
    return null;
  }

  const agencyPathFromApi = contact.agencyUrlPath ?? `${agencyId}`;
  const normalizedPath = agencyPathFromApi.startsWith('/')
    ? agencyPathFromApi
    : `/${agencyPathFromApi}`;
  const hasFullPrefix = normalizedPath.startsWith('/real-estate-agencies/');
  const agencyUrl = hasFullPrefix
    ? normalizedPath
    : `/real-estate-agencies${normalizedPath}`;

  const socialLinks = {
    web: contact.personalWebsiteUrl || contact.agencyByAgencyId?.website,
    facebook: contact.facebookUrl,
    twitter: contact.twitterUrl,
    linkedIn: contact.linkedInUrl,
    instagram: contact.instagramUrl,
    youtube:
      contact.youTubeChannelUrl || contact.agencyByAgencyId?.youtubeChannelUrl,
  };

  const profileTier =
    (contact.profileTier as PROFILE_TIERS) ?? PROFILE_TIERS.basic;

  return (
    <Hero
      desktopWidth={desktopWidth}
      profileTier={profileTier}
      backgroundImage={
        contact.agencyByAgencyId?.branding?.hero?.url ?? undefined
      }
      backgroundColour={
        contact.agencyByAgencyId?.branding?.backgroundColour ?? 'transparent'
      }
      isAgency={false}
      hasEmail={contact.hasEmail}
      phoneNumber={(contact.mobile || contact.telephone) ?? ''}
      name={contact.name ?? ''}
      setStickyActive={setStickyActive}
      emailFormComponentId={emailFormComponentId}
      renderComponent={renderComponent}
      onCallButtonClick={onCallButtonClick}
      onEmailButtonClick={onEmailButtonClick}
      bannerElement={
        <>
          {contact.profileTier === PROFILE_TIERS.platinum ||
          contact.profileTier === PROFILE_TIERS.branded ? (
            <ContactBanner
              profileTier={profileTier}
              avatarUrl={contact.profilePhoto?.url ?? ''}
              name={contact.name ?? ''}
              jobPosition={contact.jobCategory}
            />
          ) : null}
          {overallStarRating && showStarRating && (
            <div style={{ width: '100%' }}>
              <AverageStarRatingAndTotal
                section={STAR_RATING_SECTION.HERO}
                averageRating={overallStarRating.rating}
                totalReviews={overallStarRating.totalReviews}
                scrollTo
                onVendorTotalReviewsClick={onVendorTotalReviewsClick}
              />
            </div>
          )}
          <Social
            isAgency={false}
            {...socialLinks}
            onSocialClick={onSocialClick}
            isPlatinum={profileTier === PROFILE_TIERS.platinum}
          />
        </>
      }
      stickyElement={
        contact.profileTier === PROFILE_TIERS.platinum ? (
          <>
            {overallStarRating ? (
              <ContactDetailsPlatinumStickyReviews
                avatarUrl={contact.profilePhoto?.url ?? ''}
                name={contact.name ?? ''}
                jobPosition={contact.jobCategory}
                agencyName={contact.agencyName}
                isActive={isStickyActive}
                overallStarRating={overallStarRating}
                onVendorTotalReviewsClick={onVendorTotalReviewsClick}
              />
            ) : (
              <ContactDetailsSticky
                avatarUrl={contact.profilePhoto?.url ?? ''}
                name={contact.name ?? ''}
                agencyName={contact.agencyName}
                jobPosition={contact.jobCategory}
                isActive={isStickyActive}
              />
            )}
            <AgencyDetailsSticky
              isAgency={false}
              profileTier={profileTier}
              agencyName={contact.agencyName ?? ''}
              agencyUrl={agencyUrl}
              imageUrl={contact.agencyByAgencyId?.branding?.logo?.url ?? ''}
              address={contact.address?.displayAddress ?? ''}
              isActive
              hideOfficeLocation={
                contact.agencyByAgencyId?.hideOfficeLocation ?? false
              }
              onAgencyAddressClick={onAgencyAddressClick}
              onAgencyLogoClick={onAgencyLogoClick}
            />
          </>
        ) : null
      }
      sectionElement={
        <ContactDetailsSection
          profileTier={profileTier}
          avatarUrl={contact.profilePhoto?.url ?? ''}
          name={contact.name ?? ''}
          jobPosition={contact.jobCategory}
          phoneNumber={(contact.mobile || contact.telephone) ?? ''}
          hasEmail={contact.hasEmail}
          emailFormComponentId={emailFormComponentId}
          onCallButtonClick={onCallButtonClick}
          onEmailButtonClick={onEmailButtonClick}
          onVendorTotalReviewsClick={onVendorTotalReviewsClick}
          socialRender={
            profileTier === PROFILE_TIERS.basic ? (
              <Social
                inBanner={false}
                isAgency
                {...socialLinks}
                onSocialClick={onSocialClick}
              />
            ) : null
          }
          agencyDetailsRender={
            <AgencyDetailsSectionSmall
              profileTier={profileTier}
              agencyName={contact.agencyName ?? ''}
              agencyUrl={agencyUrl}
              imageUrl={contact.agencyByAgencyId?.branding?.logo?.url ?? ''}
              address={contact.address?.displayAddress ?? ''}
              isAgency={false}
              onAgencyAddressClick={onAgencyAddressClick}
              onAgencyLogoClick={onAgencyLogoClick}
            />
          }
          overallStarRating={overallStarRating}
        />
      }
    />
  );
};

ContactHeroWithFragment.fragment = FRAGMENT;
ContactHeroWithFragment.fragmentName = FRAGMENT_NAME;

export default ContactHeroWithFragment;
